<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col-sm-6">
            <h4 qid="ac-view-page-title" class="mb-3">
              {{ accessControl.name }}
            </h4>
            <div class="mb-2">
              <span v-if="getGuide('access_control')">
                <GuideButton :guide="getGuide('access_control')"></GuideButton>
              </span>
            </div>
          </div>

          <div class="col-md-6 text-right" qid="view-ac-modified">
            <div class="mb-4">
                <span class="h6 ml-3">
                  <span v-if="isGranted([permissions.ACCESS_CONTROL_EDIT, permissions.ACCESS_CONTROL_EDIT_OWN], accessControl) && (!getClient.parent || (getClient.parent && accessControl.partition))"
                        class="btn btn-edit"
                        qid="edit-ac-button"
                        @click="showEditACModal(accessControl)"
                  >
                    <font-awesome-icon icon="pen"/>
                  </span>
                  <span v-if="isGranted([permissions.ACCESS_CONTROL_DELETE, permissions.ACCESS_CONTROL_DELETE_OWN], accessControl) && (!getClient.parent || (getClient.parent && accessControl.partition))"
                        class="btn btn-delete"
                        qid="delete-ac-button"
                        @click="showConfirmDeleteModal(accessControl)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </span>
            </div>
            <div>
              <span>{{ $t('table.last_updated') }}</span>
              <span class="ml-2 text-muted small" qid="view-ac-modified-date">
                {{ accessControl.updated_at | formatDate }}
              </span>
              <Avatar v-if="accessControl.updated_by"
                      :object="accessControl.updated_by"
                      size="xs"
                      class="ml-3 mr-2"
              ></Avatar>
                <span v-if="accessControl.updated_by" qid="view-ac-modified-by">
                {{ accessControl.updated_by.name }}
              </span>
            </div>
          </div>

        </div>
      </div>
    </div>

    <b-tabs class="mt-3">

      <!-- First tab -->
      <b-tab :title="$t('maintenance.access_control.tab.basic_info')" active>
        <div class="card">
          <div class="card-body">
            <div class="content-view-body">
              <br>
              <div class="mt-3">
                <p class="text-dark">
                  {{ $t('maintenance.access_control.provisioning_label') }}
                </p>
                <hr>
                <p>
                  <span v-if="accessControl.provisioning == 1" qid="ac-provisioning-yes">
                    {{ $t('system.yes') }}
                  </span>
                  <span v-if="accessControl.provisioning == 0" qid="ac-provisioning-no">
                    {{ $t('system.no') }}
                  </span>
                </p>
                <br>
              </div>
              <div class="mt-3">
                <p class="text-dark">
                  {{ $t('maintenance.access_control.re_certification_label') }}
                </p>
                <hr>
                <p>
                  <span v-if="accessControl.re_certification == 1" qid="ac-recertification-yes">
                    {{ $t('system.yes') }}
                  </span>
                  <span v-if="accessControl.re_certification == 0" qid="ac-recertification-no">
                    {{ $t('system.no') }}
                  </span>
                </p>
                <br>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- Second tab -->
      <b-tab :title="$t('maintenance.access_control.tab.linked_systems')" class="mt-3">
        <SystemsTable :object="accessControl"></SystemsTable>
      </b-tab>
    </b-tabs>

    <!-- Edit information obligation modal template -->
    <b-modal ref="edit-access-control" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-ac-title">
          {{ $t('maintenance.access_control.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-ac-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editACFormSubmit" qid="create-ac-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-ac-name-label">
              {{ $t('maintenance.access_control.create.name_label') }}
            </label>
            <b-form-input
              qid="edit-ac-form-name"
              type="text"
              v-model="acForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.access_control.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-ac-provisioning-label">
              {{ $t('maintenance.access_control.create.provisioning_label') }}
            </label>
            <b-form-radio-group
              v-model="acForm.provisioning"
              :options="radioOptions"
              qid="edit-ac-form-provisioning"
            ></b-form-radio-group>
          </b-form-group>
          <b-form-group>
            <label qid="edit-ac-certification-label">
              {{ $t('maintenance.access_control.create.certification_label') }}
            </label>
            <b-form-radio-group
              v-model="acForm.re_certification"
              :options="radioOptions"
              qid="edit-ac-form-certification"
            ></b-form-radio-group>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditACModal"
                    qid="edit-ac-cancel-button"
          >
            {{ $t('maintenance.access_control.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-ac-submit-button"
                    v-if="isGranted([permissions.ACCESS_CONTROL_EDIT, permissions.ACCESS_CONTROL_EDIT_OWN], accessControl.created_by) && (!getClient.parent || (getClient.parent && accessControl.partition))"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.access_control.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <!-- Delete information obligation confirm modal template -->
    <b-modal ref="delete-access-control" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-ac-title">
          {{ $t('maintenance.access_control.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.access_control.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.access_control.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-ac-cancel-button"
        >
          {{ $t('maintenance.access_control.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteAC(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-ac-submit-button"
                  v-if="isGranted([permissions.ACCESS_CONTROL_DELETE, permissions.ACCESS_CONTROL_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.access_control.delete.submit') }}
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import Avatar from '@/components/Avatar.vue';
import SystemsTable from '@/views/AccessControl/SystemsTable.vue';
export default {
  name: 'AccessControlView',
  data: function() {
    return {
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        },
        {
          text: this.$t('maintenance.title'),
          to: '/maintenance'
        },
        {
          text: this.$t('maintenance.access_control.title'),
          to: '/maintenance/access-control'
        }
      ],
      accessControl: {},
      itemToDelete: {},
      acForm: {
        name: '',
        provisioning: '',
        re_certification: '',
        client:'',
      },
      error: '',
      radioOptions: [
        {text: this.$t('system.yes'), value: 1},
        {text: this.$t('system.no'), value: 0}
      ],
      buttonDisabled: false,
      myGuides: [],
    }
  },
  computed: {
    rows() {
      // Use computed value for rows because of dynamic update
      return this.totalRows
    },
    getClient() {
      return this.$store.state.client
    }
  },
  watch: {
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug !== oldValue.slug) {
        this.goToList()
      }
    }
  },
  components: {
    Avatar,
    SystemsTable
  },
  methods: {
    goToList() {
      this.$router.push('/maintenance/access-control')
    },
    getGuide(code) {
      let existingGuide = _.find(this.myGuides, function(object) {
        return object.scope == code && object.type == 'Maintenance'
      });
      return existingGuide
    },
    loadGuides(code) {
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      piincHttp.get('guides', { params:
        {
          per_page: 1,
          language: 'en',
          type: 'Maintenance',
          scope: code,
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0])
        }
      }, function() {});
    },
    openHelpCenter(object) {
      let topElement = document.getElementById("app");
      if (topElement.classList.contains("guide-collapsed")) {
        topElement.classList.toggle("guide-collapsed");
      }
      if (!topElement.classList.contains("activity-collapsed")) {
        topElement.classList.toggle("activity-collapsed");
      }

      this.$store.commit('storeHelpCenterState', true);
      this.$store.commit('storeActiveGuideScope', object);
    },
    loadContent() {
      let self = this;
      piincHttp.get('access-controls/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.accessControl = response.data;
        // Push Information obligation name in breadcrumb array
        self.breadcrumbItems.push({text: self.accessControl.name, active: true});
      }, function() {});
    },
    getUserAvatar(user){
      if (user && user.avatar) {
        return user.avatar;
      } else {
        return require('@/assets/avatar.png');
      }
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-access-control'].show()
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {}
      this.$refs['delete-access-control'].hide()
    },
    confirmDeleteAC(object) {
      let self = this;
      piincHttp.delete('access-controls/' + object.slug).then(function() {
        self.hideConfirmDeleteModal();
        self.goToList();
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('maintenance.access_control.delete.success'))
      }, function() {});
    },
    showEditACModal(object) {
      this.acForm.slug = object.slug;
      this.acForm.name = object.name;
      this.acForm.re_certification = object.re_certification;
      this.acForm.provisioning = object.provisioning;
      this.$refs['edit-access-control'].show()
    },
    hideEditACModal() {
      this.$refs['edit-access-control'].hide()
    },
    editACFormSubmit(evt) {
      this.buttonDisabled = true;
      var moment = require('moment');
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();
      this.acForm.client = this.getClient.slug;

      piincHttp.put('access-controls/' + this.acForm.slug, this.acForm).then(function(response) {
        self.hideEditACModal();
        self.buttonDisabled = false;
        // If slug is not changed, just reload content
        if (response.data.slug === self.accessControl.slug) {
          // Remove last item in breadcrumbs, because it will be added again on load content
          self.breadcrumbItems.pop();
          self.loadContent();
        } else {
          // If slug changed, reload state with new param
          self.$router.push({ name: 'maintenance.access_control.view', params: { slug: response.data.slug }})
        }
        self.$toastr('success', self.$t('maintenance.access_control.update.success'))
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
        }
      })
    }
  },
  created () {
    this.loadContent()
    this.loadGuides('access_control');
  }
}
</script>
